import React from "react";
import { Page, View, Text, Image, Link } from "@react-pdf/renderer";
import { styles, mark } from "./pdfTheme";

/*
 * The cover frame, shared with HSE and Electrical. The artwork carries no
 * wording, so one file serves all three; the product name is drawn over it.
 * Replace with src/CSA_Report_Cover.png when a designer supplies one — that is
 * the whole change.
 */
import coverArt from "../../../HSE_Report_Cover.png";

/*
 * The wordmark, for the running head. Resolved the same way the cover is:
 * react-pdf refuses a source that is not absolute and says so only in a
 * warning, which is how the cover came to render as a blank sheet.
 */
import logoArt from "../../../momentum_logo.png";

/**
 * The artwork, as a source react-pdf will actually load.
 *
 * Webpack turns the import into a root-relative path — /static/media/….png —
 * and react-pdf refuses it: **"Only absolute URLs are supported"**, logged as a
 * warning and then skipped. The page still renders, with the text in place and
 * no picture behind it, which is exactly what it looked like: a cover that had
 * simply lost its image with nothing to say why.
 *
 * Resolved against the page's own URL, so it works under a dev server and
 * under a deployed build at any path.
 */
export function absoluteSrc(art, href) {
  if (!art) return art;
  if (/^(https?:|data:|blob:|file:)/i.test(art)) return art;
  if (!href) return art;
  try {
    return new URL(art, href).href;
  } catch (e) {
    return art;
  }
}

const logoSrc = absoluteSrc(
  logoArt,
  typeof window !== "undefined" && window.location ? window.location.href : null
);

const coverSrc = absoluteSrc(
  coverArt,
  typeof window !== "undefined" && window.location ? window.location.href : null
);

/**
 * The parts of the document both PDF layouts share.
 *
 * Standard and Elaborate differ in exactly one section — the findings. Cover,
 * narrative, score analysis, overall assessment and scope are identical, so
 * they live here and are imported by both.
 *
 * That is not tidiness for its own sake. A client who receives the Standard
 * report and later asks for the Elaborate one must not find a different
 * percentage on page three; keeping one implementation makes that impossible
 * rather than merely unlikely.
 */

/* ------------------------------------------------------------------ */
/* formatting                                                          */
/* ------------------------------------------------------------------ */

export const fmtDate = (d) => {
  if (!d) return "—";
  const x = new Date(d);
  if (isNaN(x.getTime())) return String(d);
  return x.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
};

export const dateRange = (a, b) => {
  if (!a && !b) return "—";
  if (a && b && a !== b) return `${fmtDate(a)} – ${fmtDate(b)}`;
  return fmtDate(a || b);
};

export const pctText = (n) => (n === null || n === undefined ? "Not assessed" : `${n}%`);

/**
 * The line that tells two documents apart (design §7b).
 *
 *   "Issue 2 · 13 August 2026 · 159 assessed · 163 reported"
 *
 * Selection stays editable after a report goes out, so the same audit can
 * produce several documents. Without this line a client holding one of them
 * cannot say which, and neither can the auditor who sent it.
 *
 * Empty for a preview — a preview is not an issue, and numbering it would
 * either burn issue numbers on documents that never left the building or
 * print a number that means nothing.
 */
export function issueLine(header) {
  const i = header.issue;
  if (!i) return "";
  return [
    `Issue ${i.no}`,
    fmtDate(i.issued_at),
    `${i.assessed_count} assessed`,
    `${i.selected_count} reported`,
  ].join("  ·  ");
}

/* ------------------------------------------------------------------ */
/* chrome                                                              */
/* ------------------------------------------------------------------ */

/**
 * Marks every page of a preview.
 *
 * The condition is exactly the one `issueLine` uses — no `header.issue` means
 * this document was never issued. Tying both to the same field is what stops a
 * document existing that has neither a number nor a watermark.
 *
 * `fixed` puts it on every page of the block it sits in, including pages the
 * content spills onto.
 */
export function Watermark({ header }) {
  if (header && header.issue) return null;
  return (
    <View style={mark.wrap} fixed>
      <Text style={mark.text}>PREVIEW</Text>
      <Text style={mark.sub}>NOT FOR ISSUE</Text>
    </View>
  );
}

export function Chrome({ header }) {
  return (
    <>
      <Watermark header={header} />
      <View style={styles.runningHead} fixed>
        {/*
          The wordmark on every page that carries Chrome — which is every page
          except the cover, and the cover has it in the artwork already.
        */}
        <View style={styles.runningHeadLeft}>
          <Image src={logoSrc} style={styles.runningLogo} />
          <Text>
            {header.organization} — {header.site}
          </Text>
        </View>
        <Text>Critical Safety Assessment</Text>
      </View>
      <View style={styles.runningFoot} fixed>
        <Text>{header.report_id}</Text>
        <Text>{issueLine(header)}</Text>
        <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
      </View>
    </>
  );
}

export function Heading({ no, title }) {
  return (
    <View>
      <Text style={styles.h1}>
        {no}. {title}
      </Text>
      <View style={styles.h1Rule} />
    </View>
  );
}

/* ------------------------------------------------------------------ */
/* cover                                                               */
/* ------------------------------------------------------------------ */

/**
 * The cover, in the same frame HSE and Electrical use.
 *
 * The artwork carries no wording — a logo, two diagonal rules and a
 * photograph. Which report it is comes from the title drawn over it, which is
 * how one frame serves three products. Client, site and dates sit in the white
 * margin on the left, exactly where the other two put them, so a client
 * receiving all three recognises the family.
 *
 * **The photograph is HSE's, on loan.** Nothing in it is specific to HSE, so
 * it is not misleading, but CSA should have its own. When a designer supplies
 * one, drop it in as src/CSA_Report_Cover.png and change the import below —
 * that is the whole change.
 */
export function Cover({ header, summary }) {
  /*
   * The artwork is wrapped in an absolutely positioned View rather than being
   * positioned itself. react-pdf reserves flow height for a bare <Image> even
   * when it is absolute, which broke the page underneath it and sent every
   * overlay onto page two.
   *
   * Everything readable is drawn here, because the frame carries no wording —
   * which is how one image serves HSE, Electrical and CSA.
   */
  return (
    <Page size="A4" style={[styles.page, styles.coverPage]}>
      <View style={styles.coverBackdrop}>
        <Image src={coverSrc} style={styles.coverImage} />
      </View>

      {/*
        The details sit on a gold panel, not straight on the photograph. The
        first version put dark ink directly onto the picture, where it was
        unreadable — the panel is what makes the text legible, not decoration.
      */}
      <View style={styles.coverPanel}>
        <View style={styles.coverPanelInner}>
          <Row label="Client:" value={header.organization} />
          <Row label="Location:" value={header.site} />
          <Row label="Service:" value="Critical Safety Assessment" />
          <Row label="Date:" value={dateRange(header.start_date, header.end_date)} />
        </View>
      </View>

      {/* White, low, over the dark part of the image. */}
      <View style={styles.coverProduct}>
        <Text style={styles.coverProductText}>Critical Safety</Text>
        <Text style={styles.coverProductText}>Assessment</Text>
        <View style={styles.coverYearRow}>
          <View style={styles.coverYearLine} />
          <Text style={styles.coverYear}>{yearOf(header.end_date)}</Text>
        </View>
      </View>

      {/*
        After the image and the panels, so it is not painted underneath them.
        The cover is the page a reader sees first, so it is the page that most
        needs to say this is not for issue.
      */}
      <Watermark header={header} />
    </Page>
  );
}

function Row({ label, value }) {
  return (
    <View style={styles.coverDetailRow}>
      <Text style={styles.coverDetailLabel}>{label}</Text>
      <Text style={styles.coverDetailValue}>{value}</Text>
    </View>
  );
}

export function CoverDetails({ header, summary }) {
  return (
    <View>
      <View style={styles.coverRule} />
      <Text style={styles.detailsHead}>{header.organization}</Text>
      <Text style={styles.detailsSite}>{header.site}</Text>

      <View style={styles.coverGrid}>
        <Cell label="ASSESSMENT DATES" value={dateRange(header.start_date, header.end_date)} />
        <Cell
          label="OVERALL CONFORMANCE"
          value={
            pctText(summary.overall_pct) +
            (summary.maturity_band ? " — " + summary.maturity_band : "")
          }
        />
        <Cell label="PREPARED BY" value={header.prepared_by || "—"} />
        {/*
          Stated plainly rather than left blank. Review is optional and never
          blocks issuing a report (§7c), which is only honest if the document
          says which of the two happened.
        */}
        <Cell
          label="REVIEWED BY"
          value={header.was_reviewed ? header.reviewed_by || "Reviewed" : "Not reviewed"}
        />
        <Cell label="FRAMEWORK VERSION" value={header.framework_version} />
        <Cell label="REPORT REFERENCE" value={header.report_id} />
      </View>
    </View>
  );
}

function Cell({ label, value }) {
  return (
    <View style={styles.coverCell}>
      <Text style={styles.coverLabel}>{label}</Text>
      <Text style={styles.coverValue}>{value}</Text>
    </View>
  );
}

/** The year the assessment finished, not today's — a report reprinted next
    January must not relabel itself. */
function yearOf(d) {
  const y = String(d || "").slice(0, 4);
  return /^[0-9]{4}$/.test(y) ? y : String(new Date().getFullYear());
}

/* ------------------------------------------------------------------ */
/* narrative                                                           */
/* ------------------------------------------------------------------ */

/**
 * Auditor prose. Stored as plain text, so paragraphs are split on blank lines
 * rather than parsed as HTML — there is no rich-text editor behind this yet,
 * and pretending otherwise would print markup at a client.
 */
export function Narrative({ section }) {
  const paras = String(section.body || "")
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);

  return (
    <View>
      <Heading no={section.no} title={section.title} />
      {paras.length ? (
        paras.map((p, i) => (
          <Text key={i} style={styles.body}>
            {p}
          </Text>
        ))
      ) : (
        <Text style={styles.empty}>This section has not been written.</Text>
      )}
    </View>
  );
}

/* ------------------------------------------------------------------ */
/* 3 — score analysis                                                  */
/* ------------------------------------------------------------------ */

export function ScoreAnalysis({ section }) {
  const d = section.data;

  return (
    <View>
      <Heading no={section.no} title={section.title} />

      <View style={styles.scoreHero}>
        <Text style={styles.scoreBig}>{pctText(d.overall.pct)}</Text>
        <View>
          <Text style={styles.scoreBand}>{d.overall.band}</Text>
          <Text style={styles.muted}>
            {d.overall.assessed} of {d.overall.total} questions assessed
            {d.overall.not_assessed ? ` · ${d.overall.not_assessed} out of scope` : ""}
          </Text>
        </View>
      </View>

      <Text style={styles.h2}>By pillar</Text>
      <View>
        <View style={[styles.tRow, styles.tHead]}>
          <Text style={[styles.tHeadCell, { width: "8%" }]}>#</Text>
          <Text style={[styles.tHeadCell, { width: "50%" }]}>PILLAR</Text>
          <Text style={[styles.tHeadCell, { width: "14%" }]}>ASSESSED</Text>
          <Text style={[styles.tHeadCell, { width: "14%" }]}>SCORE</Text>
          <Text style={[styles.tHeadCell, { width: "14%" }]}>BAND</Text>
        </View>
        {d.pillars.map((p, i) => (
          <View key={p.no} style={[styles.tRow, i % 2 ? styles.zebra : {}]} wrap={false}>
            <Text style={[styles.tCell, { width: "8%" }]}>{p.no}</Text>
            <Text style={[styles.tCell, { width: "50%" }]}>{p.name}</Text>
            <Text style={[styles.tCell, { width: "14%" }]}>
              {p.assessed}/{p.total}
            </Text>
            <Text style={[styles.tCell, { width: "14%", fontWeight: 700 }]}>{pctText(p.pct)}</Text>
            <Text style={[styles.tCell, { width: "14%" }]}>{p.band}</Text>
          </View>
        ))}
      </View>

      <Text style={styles.h2} break>
        By sub-pillar
      </Text>
      {d.pillars.map((p) => (
        <View key={p.no} wrap={false}>
          <Text style={styles.h3}>
            {p.no}. {p.name}
          </Text>
          {p.sub_pillars.map((s, i) => (
            <View key={s.name} style={[styles.tRow, i % 2 ? styles.zebra : {}]}>
              <Text style={[styles.tCellTight, { width: "58%" }]}>{s.name}</Text>
              <Text style={[styles.tCellTight, { width: "14%" }]}>
                {s.assessed}/{s.total}
              </Text>
              <Text style={[styles.tCellTight, { width: "14%", fontWeight: 700 }]}>
                {pctText(s.pct)}
              </Text>
              <Text style={[styles.tCellTight, { width: "14%" }]}>{s.band}</Text>
            </View>
          ))}
        </View>
      ))}
    </View>
  );
}

/* ------------------------------------------------------------------ */
/* 4 — overall assessment                                              */
/* ------------------------------------------------------------------ */

export function OverallAssessment({ section }) {
  const d = section.data;
  return (
    <View>
      <Heading no={section.no} title={section.title} />
      <View style={styles.scoreHero}>
        <Text style={styles.scoreBig}>{pctText(d.pct)}</Text>
        <Text style={styles.scoreBand}>{d.band}</Text>
      </View>
      <Text style={styles.body}>{d.statement}</Text>

      <View style={[styles.tRow, styles.tHead, { marginTop: 10 }]}>
        <Text style={[styles.tHeadCell, { width: "25%" }]}>CONFORMANT</Text>
        <Text style={[styles.tHeadCell, { width: "25%" }]}>PARTIAL</Text>
        <Text style={[styles.tHeadCell, { width: "25%" }]}>NON-CONFORMANT</Text>
        <Text style={[styles.tHeadCell, { width: "25%" }]}>NOT APPLICABLE</Text>
      </View>
      <View style={styles.tRow}>
        <Text style={[styles.tCell, { width: "25%" }]}>{d.counts.conformant}</Text>
        <Text style={[styles.tCell, { width: "25%" }]}>{d.counts.partial}</Text>
        <Text style={[styles.tCell, { width: "25%" }]}>{d.counts.non_conformant}</Text>
        <Text style={[styles.tCell, { width: "25%" }]}>{d.counts.not_applicable}</Text>
      </View>
    </View>
  );
}

/* ------------------------------------------------------------------ */
/* 5 — scope                                                           */
/* ------------------------------------------------------------------ */

export function Scope({ section }) {
  const d = section.data;
  return (
    <View>
      <Heading no={section.no} title={section.title} />
      <Text style={styles.body}>{d.note}</Text>
      <Text style={styles.muted}>
        {d.assessed} of {d.total} questions were assessed
        {d.excluded ? `; ${d.excluded} were excluded.` : "."}
      </Text>

      {d.exclusions.length ? (
        <View style={{ marginTop: 10 }}>
          {d.exclusions.map((e, i) => (
            <View key={i} style={styles.scopeItem} wrap={false}>
              <Text style={styles.scopeName}>{e.name}</Text>
              <Text style={styles.muted}>{e.reason}</Text>
            </View>
          ))}
        </View>
      ) : null}
    </View>
  );
}
